import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronDown, MapPin } from 'lucide-react';
import Logo from '../Logo';
import { useCity } from '../../contexts/CityContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUnified } from '../../contexts/UnifiedContext';
import { resolveAccountAvatarDisplay } from '../../utils/photoResolver';
import { getUserCoordsOnce, GEO_PERMISSION_DENIED } from '../../utils/userGeolocation';
import { pickNearestLiveCity } from '../../utils/geo';
import { analyticsService } from '../../services/analytics.service';
import { CITY_CONFIG, CITY_ORDER } from '../../config/cities';
import VenueNominationModal from '../shared/VenueNominationModal';
import { useToast } from '../shared/ToastNotification';
import '../../styles/desktop-homepage-replica.css';

const DesktopHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCity, setSelectedCity } = useCity();
  const { isAuthenticated, user, signOut } = useAuth();
  const { unified, photoReviews } = useUnified();
  const { showInfo, showSuccess } = useToast();
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNominationModal, setShowNominationModal] = useState(false);
  const [nominationPageType, setNominationPageType] = useState('stag');
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [avatarImageBroken, setAvatarImageBroken] = useState(false);
  const cityDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  const fridayProfile = unified?.friday_swipe || null;
  const { photoUrl, initials, hasPhoto, isPendingReview } = resolveAccountAvatarDisplay({
    user,
    unifiedUser: unified?.user ?? null,
    fridaySwipeProfile: fridayProfile,
    photoReviews,
  });

  const liveCities = CITY_ORDER.filter((city) => {
    const cfg = CITY_CONFIG[city] || {};
    return cfg.work || cfg.stag;
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCityDropdown && cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
      if (showUserDropdown && userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCityDropdown, showUserDropdown]);

  useEffect(() => {
    setPhotoLoaded(false);
    setAvatarImageBroken(false);
  }, [photoUrl]);

  useEffect(() => {
    const handler = (event) => {
      const pageType = event?.detail?.pageType || 'stag';
      setNominationPageType(pageType);
      setShowNominationModal(true);
    };
    window.addEventListener('openVenueNomination', handler);
    return () => window.removeEventListener('openVenueNomination', handler);
  }, []);

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setShowCityDropdown(false);
    try { analyticsService.track('city_change', { city, source: 'desktop_header' }); } catch (_) {}
  };

  const handleListVenue = () => {
    const path = location.pathname || '/';
    const pageType = path.startsWith('/work') ? 'work' : 'stag';
    try { analyticsService.track('header_cta_click', { cta: 'list_venue' }); } catch (_) {}
    setNominationPageType(pageType);
    setShowNominationModal(true);
  };

  const handleSignIn = () => {
    try { analyticsService.track('header_cta_click', { cta: 'sign_in' }); } catch (_) {}
    navigate('/');
    setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('openAuthPrompt', { detail: { context: 'header_signin' } })); } catch (_) {}
    }, 100);
  };

  return (
    <header className="dr-desktop-header">
      <div className="dr-desktop-header__container">
        <div className="dr-desktop-header__logo" onClick={() => navigate('/')}>
          <Logo variant="full" size="medium" className="logo-glow-animated" />
        </div>

        <div className="dr-desktop-header__actions">
          <button className="dr-header-btn dr-header-btn--ghost" onClick={handleListVenue}>
            <Building2 className="w-4 h-4" />
            <span>List Your Venue</span>
          </button>

          <div className="dr-header-location" ref={cityDropdownRef}>
            <button
              className="dr-header-location__trigger"
              onClick={() => setShowCityDropdown((prev) => !prev)}
              aria-label="Change city"
            >
              <MapPin className="w-4 h-4 text-emerald-400" />
              <span className="dr-header-location__city">{selectedCity}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showCityDropdown && (
              <div className="dr-header-location__dropdown">
                <button
                  type="button"
                  className="dr-header-location__option dr-header-location__option--detect"
                  onClick={async () => {
                    setShowCityDropdown(false);
                    const res = await getUserCoordsOnce({ enableHighAccuracy: true, timeout: 15000, maximumAge: 120000 });
                    if (!res.ok) {
                      if (res.code === GEO_PERMISSION_DENIED) {
                        showInfo('Location access was denied. Pick your city from the list.', { title: 'City', duration: 4200 });
                      } else {
                        showInfo('We couldn\'t detect your location. Pick your city from the list.', { title: 'Location', duration: 4200 });
                      }
                      return;
                    }
                    const r = pickNearestLiveCity(res.lat, res.lng);
                    if (r.city && CITY_CONFIG[r.city]) {
                      setSelectedCity(r.city);
                      showSuccess(`Viewing ${r.city} based on your location.`, { title: 'City', duration: 3200 });
                    }
                  }}
                >
                  <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                  Use My Location
                </button>
                <div className="h-px bg-white/10 mx-3 my-1" />
                {liveCities.map((city) => (
                  <button
                    key={city}
                    className={`dr-header-location__option ${city === selectedCity ? 'dr-header-location__option--active' : ''}`}
                    onClick={() => handleCitySelect(city)}
                  >
                    {city}
                    {city === selectedCity && <Check className="w-4 h-4 text-emerald-400" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="dr-header-user" ref={userDropdownRef}>
            <button
              className="dr-header-user__trigger"
              onClick={() => setShowUserDropdown((prev) => !prev)}
              aria-label="Open profile menu"
            >
              {!isAuthenticated ? (
                <picture>
                  <source srcSet="/logo-mascot.webp" type="image/webp" />
                  <img src="/logo-mascot.png" alt="STAGSTER" className="dr-header-user__mascot" />
                </picture>
              ) : (
                <span className="relative inline-flex h-full w-full items-center justify-center">
                  {hasPhoto && photoUrl && !avatarImageBroken ? (
                    <>
                      <img
                        src={photoUrl}
                        alt="Profile"
                        className={`dr-header-user__avatar transition-opacity duration-200 ${photoLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setPhotoLoaded(true)}
                        onError={() => {
                          setPhotoLoaded(false);
                          setAvatarImageBroken(true);
                        }}
                      />
                      {!photoLoaded && (
                        <span className="dr-header-user__initials absolute inset-0 flex items-center justify-center">
                          {initials}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="dr-header-user__initials">
                      {initials}
                    </span>
                  )}
                  {isPendingReview ? (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold text-black shadow ring-1 ring-black/25"
                      title="Photo pending review"
                    >
                      ?
                    </span>
                  ) : null}
                </span>
              )}
            </button>

            {showUserDropdown && (
              <div className="dr-header-user__dropdown">
                {isAuthenticated ? (
                  <>
                    <div className="dr-user-menu__header">
                      <span className="dr-user-menu__label">My Account</span>
                      <span className="dr-user-menu__email">{user?.email || ''}</span>
                    </div>
                    <div className="dr-user-menu__divider" />
                    <button className="dr-user-menu__item" onClick={() => { navigate('/profile'); setShowUserDropdown(false); }}>
                      View Profile
                    </button>
                    <button className="dr-user-menu__item" onClick={() => { navigate('/matches'); setShowUserDropdown(false); }}>
                      My Matches & Chats
                    </button>
                    <div className="dr-user-menu__divider" />
                    <button className="dr-user-menu__item" onClick={() => { navigate('/faq'); setShowUserDropdown(false); }}>
                      Help & FAQ
                    </button>
                    <div className="dr-user-menu__divider" />
                    <button
                      className="dr-user-menu__item dr-user-menu__item--danger"
                      onClick={async () => {
                        try {
                          await signOut();
                        } catch (_) {}
                        setShowUserDropdown(false);
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <div className="dr-user-menu__header">
                      <span className="dr-user-menu__label">Get Started</span>
                    </div>
                    <button className="dr-user-menu__item dr-user-menu__item--primary" onClick={() => { handleSignIn(); setShowUserDropdown(false); }}>
                      Sign In
                    </button>
                    <div className="dr-user-menu__divider" />
                    <button className="dr-user-menu__item" onClick={() => { navigate('/about'); setShowUserDropdown(false); }}>
                      About STAGSTER
                    </button>
                    <button className="dr-user-menu__item" onClick={() => { navigate('/safety'); setShowUserDropdown(false); }}>
                      Safety & Trust
                    </button>
                    <button className="dr-user-menu__item" onClick={() => { navigate('/faq'); setShowUserDropdown(false); }}>
                      Help & FAQ
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <VenueNominationModal
        isOpen={showNominationModal}
        onClose={() => setShowNominationModal(false)}
        pageType={nominationPageType}
      />
    </header>
  );
};

export default DesktopHeader;